import React, { useEffect, useState, useRef } from 'react';
import { browserHistory } from 'react-router';
import { Events, handleAnalytics } from '@foxcorp/delta-web-analytics';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tab from 'screens/Find/components/Tab';
import { scrollTop } from 'screens/Find/components/FindProcessor';
import Icon from 'componentsV2/Icon';
import { isFilterActive } from './FilterHelper';
import Styles from './Filter.module.scss';

const Filter = ({
  filters = [],
  isScrolled,
  isCarousel,
  theme = {},
  setSearchValue,
  setFetchingSearch,
}) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showArrows, setShowArrows] = useState(false);
  const [showForwardArrow, setShowForwardArrow] = useState(true);
  const filterListRef = useRef(null);
  const lastElementRef = useRef(null);
  const filterLength = filters?.length ?? 0;
  const currentPath = window?.location?.pathname?.split('/')[1];

  useEffect(() => {
    if (filterLength) {
      const filterListWidth = filterListRef?.current?.offsetWidth;
      const filterTabs = filterListRef?.current?.children;
      let tabsWidth = 0;
      [...Array(filterTabs.length)].forEach((_, i) => {
        const gapWidth = 15;
        tabsWidth = tabsWidth + gapWidth + filterTabs[i]?.offsetWidth;
      });
      if (tabsWidth >= filterListWidth) {
        setShowArrows(true);
      } else {
        setShowArrows(false);
      }
    }
  }, [filterLength]);

  const observer = useRef(
    new window.IntersectionObserver(entries => {
      const first = entries[0];
      if (first.isIntersecting) {
        setShowForwardArrow(false);
      } else {
        setShowForwardArrow(true);
      }
    }),
  );

  useEffect(() => {
    const currentElement = lastElementRef.current;
    const currentObserver = observer.current;
    if (currentElement) {
      currentObserver.observe(currentElement);
    }
    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, []);

  const { pathname: pathName = '' } = window.location;

  const onClickTab = (value = '') => {
    const path = `/${currentPath}/${value}`.toLowerCase();
    handleAnalytics({
      event: Events.BUTTON_LINK_SELECTED,
      payload: {
        page_button_text: value,
        page_name: `delta:web:${currentPath}:landing`,
        page_type: `${currentPath}:section-front`,
      },
    });
    if (currentPath === 'find') {
      setFetchingSearch(true);
      setSearchValue(value.toLowerCase());
    }
    browserHistory.push(path);
    scrollTop();
  };

  const renderItems = () =>
    filters.map((filter, idx) => {
      const activeTab = isFilterActive(pathName, filter);
      let autoSelectTab = idx === 0;
      if (currentPath === 'find') autoSelectTab = false;
      const title = filter?.headline ?? filter;
      return (
        <Tab
          isCarousel={isCarousel}
          autoSelectTab={autoSelectTab}
          activeTab={activeTab}
          key={title}
          onClickTab={onClickTab}
          title={title}
          scrollPosition={scrollPosition}
        />
      );
    });

  const onForwardArrowClick = () => {
    setScrollPosition(scrollPosition - 200);
    handleAnalytics({
      event: Events.BUTTON_LINK_SELECTED,
      payload: {
        page_button_text: `${currentPath} page forward arrow clicked`,
        page_collection_title: currentPath,
      },
    });
  };

  const onBackArrowClick = () => {
    setScrollPosition(scrollPosition + 200);
    handleAnalytics({
      event: Events.BUTTON_LINK_SELECTED,
      payload: {
        page_button_text: `${currentPath} page back arrow clicked`,
        page_collection_title: currentPath,
      },
    });
  };

  return (
    <div
      className={classNames(
        Styles.filterContainer,
        theme.filterContainer,
        isCarousel ? Styles.isCarousel : '',
      )}
    >
      {scrollPosition !== 0 && (
        <div
          className={classNames(Styles.filterArrow, Styles.backArrow)}
          onClick={onBackArrowClick}
        >
          <Icon icon="back-arrow" />
        </div>
      )}
      <div
        ref={filterListRef}
        className={classNames(
          Styles.filterList,
          isScrolled && Styles.scrolled,
          currentPath !== 'browse' ? Styles.recentTabs : '',
        )}
      >
        {renderItems()}
        <div
          style={{ transform: `translateX(${scrollPosition}px)` }}
          ref={lastElementRef}
          aria-hidden
          className={Styles.hiddenElement}
        />
      </div>
      {showArrows && showForwardArrow && (
        <div
          className={classNames(Styles.filterArrow, Styles.forwardArrow)}
          onClick={onForwardArrowClick}
        >
          <Icon icon="forward-arrow" />
        </div>
      )}
    </div>
  );
};

Filter.propTypes = {
  filters: PropTypes.arrayOf(PropTypes.string),
  isCarousel: PropTypes.bool,
  isScrolled: PropTypes.bool,
  setFetchingSearch: PropTypes.func,
  setSearchValue: PropTypes.func,
  theme: PropTypes.shape({}),
};

export default Filter;
